import { useState, useEffect, useCallback, useRef } from "react";
import axiosInstance from "../utils/axiosConfig";
import { useHistory, useLocation } from "react-router-dom";
import Popper from '@material-ui/core/Popper';
import MenuList from '@material-ui/core/MenuList';
import Paper from '@material-ui/core/Paper';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Badge from "@material-ui/core/Badge";
import IconButton from "@material-ui/core/IconButton";
import MenuItem from "@material-ui/core/MenuItem";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import Grid from "@material-ui/core/Grid";
import CircularProgress from "@material-ui/core/CircularProgress";
import { makeStyles } from "@material-ui/core/styles";
import BrAvatar from "../Profile/Avatar";
import Home from "../Profile/Home";
import Journal from "../Profile/Journal";
import XCPals from "../Profile/XCPals";
import NavTabs from "./NavTabs";
import ParentsPortal from "../Profile/ParentsPortal";
import LifeTopics from "../Profile/LifeTopics";
import OnlineResources from "../Profile/OnlineResources";
import { usePusherConnection } from '../hooks/usePusherConnection';

const { REACT_APP_API_URL } = process.env;

const useStyles = makeStyles((theme) => ({
    badge: {
        right: 8,
        top: 8,
        border: `2px solid ${theme.palette?.primary?.main || '#1976d2'}`,
        padding: "0 4px",
        minWidth: '20px',
        height: '20px',
        fontSize: '12px',
        fontWeight: 'bold',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    notifBadge: {
        right: 1,
        top: 15,
        border: `2px solid ${theme.primaryColor}`,
        padding: "0 4px",
        justifyContent: "flex-end",
    },
    root: {
        flexGrow: 1,
        justifyContent: "center",
    },
    topNav: {
        height: "75px",
        width: "100%",
    },
    control: {
        padding: theme.spacing(2),
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
    },
    menuPaper: {
        opacity: 0,
        transform: 'scale(0.95)',
        transition: 'opacity 200ms ease-in-out, transform 200ms ease-in-out',
    },
    menuPaperVisible: {
        opacity: 1,
        transform: 'scale(1)',
    }
}));

export default function Dashboard(props) {
    const [settings, setSettings] = useState(null);
    
    const { user, isParent, isMentor, showNotification, theme, isLoading: propsIsLoading, initialTab } = props;
    
    // Debug avatar data received by Dashboard
    console.log('Dashboard: Received user prop with avatar data:', {
        user_id: user?.id,
        avatar_id: user?.avatar_id,
        avatar_object: user?.avatar,
        avatar_url: user?.avatar?.url,
        component: 'Dashboard',
        timestamp: new Date().toISOString()
    });
    
    const [value, setValue] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [messageCount, setMessageCount] = useState(() => {
        // SOLUTION 6: Check sessionStorage first for recent message count
        // This prevents stale initialization from user.message_notifs on remount
        const sessionCount = sessionStorage.getItem('messageCount');
        const sessionTimestamp = sessionStorage.getItem('messageCountTimestamp');
        const sessionUserId = sessionStorage.getItem('messageCountUserId');
        
        // Use sessionStorage if:
        // 1. It exists
        // 2. It's for the current user
        // 3. It's recent (within last 5 minutes)
        const now = Date.now();
        const fiveMinutes = 5 * 60 * 1000;
        const isRecentSession = sessionTimestamp && (now - parseInt(sessionTimestamp) < fiveMinutes);
        const isCurrentUser = sessionUserId && sessionUserId === String(user?.id);
        
        if (sessionCount && isRecentSession && isCurrentUser) {
            const count = parseInt(sessionCount);
            console.log('Dashboard: Using sessionStorage count:', count, 'from timestamp:', new Date(parseInt(sessionTimestamp)).toISOString());
            return count;
        }
        
        // Fallback to user.message_notifs if no recent sessionStorage
        const initialCount = user?.message_notifs?.length || 0;
        console.log('Dashboard: Initial message count state:', initialCount, 'from user.message_notifs:', user?.message_notifs);
        console.log('Dashboard: Full user object at initialization:', user);
        return initialCount;
    });
    
    // Track when messages were last read to prevent race conditions
    // SOLUTION 6: Restore from sessionStorage to preserve across page refreshes
    const [lastMessagesReadTime, setLastMessagesReadTime] = useState(() => {
        const storedTime = sessionStorage.getItem('lastMessagesReadTime');
        const storedTimestamp = sessionStorage.getItem('lastMessagesReadTimeTimestamp');
        const storedUserId = sessionStorage.getItem('lastMessagesReadTimeUserId');
        
        // Only restore if it's recent (within 15 seconds) and for current user
        if (storedTime && storedTimestamp && storedUserId === String(user?.id)) {
            const timeSinceRead = Date.now() - parseInt(storedTimestamp);
            if (timeSinceRead < 15000) { // Within protection window
                const restoredTime = parseInt(storedTime);
                console.log('Dashboard: Restored lastMessagesReadTime from sessionStorage:', new Date(restoredTime).toISOString());
                return restoredTime;
            }
        }
        return 0;
    });
    
    // Ref to track last messagesRead event time (for use in closures)
    const lastMessagesReadTimeRef = useRef(lastMessagesReadTime);
    
    // Update ref when state changes
    useEffect(() => {
        lastMessagesReadTimeRef.current = lastMessagesReadTime;
    }, [lastMessagesReadTime]);
    
    // Track if this is a fresh login (user just logged in) to force immediate refresh
    const isFreshLoginRef = useRef(false);
    
    // Track pending refresh requests to cancel them when needed
    const refreshAbortControllerRef = useRef(null);
    
    // Track if periodic refresh should be paused (e.g., after messagesRead event)
    const pausePeriodicRefreshRef = useRef(false);
    
    // Debounce timer for refresh calls
    const refreshDebounceTimerRef = useRef(null);
    
    const classes = useStyles();
    const history = useHistory();
    const location = useLocation();
    
    // Track previous location to detect navigation (must be after location is declared)
    const prevLocationRef = useRef(location.pathname);
    
    // Initialize settings from multiple sources with fallbacks
    useEffect(() => {
        const initializeSettings = () => {
            // Try to get settings from localStorage first
            let loadedSettings = null;
            try {
                const storedSettings = window.localStorage.getItem('SETTINGS');
                if (storedSettings && storedSettings !== 'null') {
                    loadedSettings = JSON.parse(storedSettings);
                }
            } catch (error) {
                console.warn('Dashboard: Error parsing stored settings:', error);
            }
            
            // Fallback to user.settings if localStorage doesn't have valid settings
            if (!loadedSettings && user?.settings) {
                console.log('Dashboard: Using user.settings as fallback');
                loadedSettings = user.settings;
                // Save to localStorage for future use
                try {
                    window.localStorage.setItem('SETTINGS', JSON.stringify(user.settings));
                } catch (error) {
                    console.warn('Dashboard: Error saving settings to localStorage:', error);
                }
            }
            
            // Default fallback settings for new users
            if (!loadedSettings) {
                console.log('Dashboard: Using default settings');
                loadedSettings = {
                    chat_allowed: 1, // Enable chat by default
                    online_resources: 1,
                    daily_hours: 8,
                    profile_hidden: 0
                };
                try {
                    window.localStorage.setItem('SETTINGS', JSON.stringify(loadedSettings));
                } catch (error) {
                    console.warn('Dashboard: Error saving default settings:', error);
                }
            }
            
            console.log('Dashboard: Initialized settings:', loadedSettings);
            setSettings(loadedSettings);
        };
        
        if (user) {
            initializeSettings();
        }
    }, [user]);
    
    useEffect(() => {
        console.log('Dashboard: useEffect triggered for user initialization', {
            propsIsLoading,
            user: !!user,
            userId: user?.id,
            messageNotifs: user?.message_notifs?.length,
            messageNotifsArray: user?.message_notifs
        });
        
        if (!propsIsLoading && user) {
            // Check if user is a mentor - mentors don't participate in regular messaging
            const isMentorUser = user?.roles?.some(role => role.name === "mentor");
            
            if (isMentorUser) {
                console.log('Dashboard: Setting message count to 0 for mentor user');
                setMessageCount(0); // Mentors always have 0 message count
                // Update sessionStorage for mentors
                sessionStorage.setItem('messageCount', '0');
                sessionStorage.setItem('messageCountTimestamp', String(Date.now()));
                sessionStorage.setItem('messageCountUserId', String(user.id));
            } else {
                // SOLUTION 6: Skip initialization from stale user.message_notifs if messages were recently read
                const timeSinceLastRead = Date.now() - lastMessagesReadTimeRef.current;
                const recentlyReadThreshold = 15000; // 15 seconds - same as protection window
                const messagesRecentlyRead = lastMessagesReadTimeRef.current > 0 && timeSinceLastRead < recentlyReadThreshold;
                
                // Check sessionStorage for recent count
                const sessionCount = sessionStorage.getItem('messageCount');
                const sessionTimestamp = sessionStorage.getItem('messageCountTimestamp');
                const sessionUserId = sessionStorage.getItem('messageCountUserId');
                const now = Date.now();
                const fiveMinutes = 5 * 60 * 1000;
                const isRecentSession = sessionTimestamp && (now - parseInt(sessionTimestamp) < fiveMinutes);
                const isCurrentUser = sessionUserId && sessionUserId === String(user.id);
                const hasValidSession = sessionCount && isRecentSession && isCurrentUser;
                
                if (messagesRecentlyRead || hasValidSession) {
                    // Messages were recently read OR we have valid sessionStorage
                    // Skip initialization from potentially stale user.message_notifs
                    // Use sessionStorage if available, otherwise keep current state (which should be 0 from messagesRead)
                    const countToUse = hasValidSession ? parseInt(sessionCount) : messageCount;
                    console.log('Dashboard: Skipping stale initialization from user.message_notifs', {
                        reason: messagesRecentlyRead ? 'messages_recently_read' : 'valid_session_storage',
                        timeSinceRead: messagesRecentlyRead ? `${timeSinceLastRead}ms` : null,
                        usingCount: countToUse,
                        sessionStorageCount: hasValidSession ? sessionCount : null
                    });
                    
                    // Only update if different from current state
                    if (countToUse !== messageCount) {
                        setMessageCount(countToUse);
                    }
                } else {
                    // No recent read activity and no valid sessionStorage
                    // Safe to initialize from user.message_notifs (but this might be stale)
                    // We'll still force a refresh below to ensure accuracy
                    const initialCount = user.message_notifs?.length || 0;
                    console.log('Dashboard: Setting initial message count for non-mentor user to:', initialCount);
                    console.log('Dashboard: Full user.message_notifs array:', user.message_notifs);
                    setMessageCount(initialCount);
                    
                    // Update sessionStorage with initial count
                    sessionStorage.setItem('messageCount', String(initialCount));
                    sessionStorage.setItem('messageCountTimestamp', String(Date.now()));
                    sessionStorage.setItem('messageCountUserId', String(user.id));
                }
            }
            
            setIsLoading(false);
        }
    }, [user, propsIsLoading, messageCount]);
    
    const handleTabChange = useCallback((newValue) => {
        setValue(newValue);
    }, []);
    
    useEffect(() => {
        const path = location.pathname.split('/').pop();
        switch (path) {
            case 'home':
                setValue(0);
                break;
            case 'life_topics':
                setValue(1);
                break;
            case 'xc_pals':
                setValue(2);
                break;
            case 'journal':
                setValue(3);
                break;
            case 'resources':
                setValue(4);
                break;
            case 'parents':
                setValue(5);
                break;
            default:
                setValue(0);
        }
    }, [location]);
    
    // Cancel any pending refresh requests (must be defined before useEffects that use it)
    const cancelPendingRefresh = useCallback(() => {
        if (refreshAbortControllerRef.current) {
            refreshAbortControllerRef.current.abort();
            refreshAbortControllerRef.current = null;
        }
        if (refreshDebounceTimerRef.current) {
            // Handle both old format (just timer ID) and new format (object with timerId and startTime)
            if (typeof refreshDebounceTimerRef.current === 'object' && refreshDebounceTimerRef.current.timerId) {
                clearTimeout(refreshDebounceTimerRef.current.timerId);
            } else {
                clearTimeout(refreshDebounceTimerRef.current);
            }
            refreshDebounceTimerRef.current = null;
        }
    }, []);
    
    // Detect navigation and pause refresh during transitions to prevent race conditions
    useEffect(() => {
        const currentPath = location.pathname;
        const prevPath = prevLocationRef.current;
        
        // If navigation occurred (path changed)
        if (currentPath !== prevPath) {
            console.log('Dashboard: Navigation detected', { from: prevPath, to: currentPath });
            
            // Cancel any pending refresh requests
            cancelPendingRefresh();
            
            // Pause periodic refresh briefly during navigation
            pausePeriodicRefreshRef.current = true;
            
            // Resume after navigation completes (allow time for route to mount)
            setTimeout(() => {
                pausePeriodicRefreshRef.current = false;
                console.log('Dashboard: Periodic refresh resumed after navigation');
            }, 1000); // 1 second pause during navigation
            
            // Update previous location
            prevLocationRef.current = currentPath;
        }
    }, [location.pathname, cancelPendingRefresh]);
    
    const { isConnected, subscribeToMenuNotifications, subscribeToMessageNotifications } = usePusherConnection(user);
    
    useEffect(() => {
        if (isConnected && user?.id) {
            const cleanup = subscribeToMenuNotifications((e) => {
                if (e.message) {
                    setMessageCount(prevCount => prevCount + 1);
                }
            });
            
            return () => {
                if (cleanup) cleanup();
            };
        }
    }, [isConnected, user?.id, subscribeToMenuNotifications]);
    
    // Subscribe to message notifications for the message counter badge
    useEffect(() => {
        if (isConnected && user?.id) {
            // Setup message notification subscription
            
            const cleanup = subscribeToMessageNotifications((eventData) => {
                // Handle different event structures
                let hasValidMessage = false;
                let messageData = null;
                
                // Check various possible structures for the message
                if (eventData.message && typeof eventData.message === 'object') {
                    hasValidMessage = true;
                    messageData = eventData.message;
                } else if (eventData.data && eventData.data.message && typeof eventData.data.message === 'object') {
                    hasValidMessage = true;
                    messageData = eventData.data.message;
                } else if (eventData.sender_id && eventData.recipient_id && eventData.content) {
                    // Direct message object
                    hasValidMessage = true;
                    messageData = eventData;
                }
                
                if (hasValidMessage && messageData) {
                    // Check if this message is for the current user (or selected child for parents)
                    const isParentUser = user.roles?.some(role => role.name === "parent");
                    const targetUserId = isParentUser ? 
                        parseInt(window.localStorage.getItem('SELECTED_CHILD_ID')) : 
                        user.id;
                    
                    // Message validation for target user
                    
                    // Only increment counter if message is for the target user
                    if (messageData.recipient_id === targetUserId) {
                        // Check if user is on messenger page
                        // If yes, MessengerPage will auto-mark messages in the current conversation
                        // and dispatch messagesRead event, so we should skip increment here
                        const currentPath = window.location.pathname;
                        const isOnMessengerPage = currentPath.includes('/messenger') || currentPath.includes('/messenger/');
                        
                        if (isOnMessengerPage) {
                            // User is on messenger page
                            // MessengerPage will auto-mark messages in the current conversation
                            // For messages in current conversation: auto-mark happens, messagesRead event updates counter
                            // For messages in other conversations: should show badge, but we'll delay increment
                            // to allow MessengerPage to check if it's in current conversation first
                            const messageReceivedTime = Date.now();
                            setTimeout(() => {
                                // Check if messagesRead event was dispatched (meaning message was auto-marked)
                                // Use ref to avoid closure issues with state
                                const timeSinceLastRead = Date.now() - lastMessagesReadTimeRef.current;
                                
                                // If messagesRead happened very recently (within 1 second), message was auto-marked
                                // Don't increment counter - it was already handled by messagesRead event
                                if (lastMessagesReadTimeRef.current > messageReceivedTime - 1000) {
                                    console.log('Dashboard: Skipping counter increment - message was auto-marked by MessengerPage', {
                                        timeSinceRead: timeSinceLastRead,
                                        messageReceivedTime: messageReceivedTime,
                                        lastReadTime: lastMessagesReadTimeRef.current
                                    });
                                    return;
                                }
                                
                                // Message wasn't auto-marked (likely from different conversation)
                                // Increment counter to show badge
                                setMessageCount(prevCount => {
                                    const newCount = prevCount + 1;
                                    console.log('Dashboard: Message count updated from', prevCount, 'to', newCount, '(new message from different conversation while on messenger page)');
                                    
                                    // SOLUTION 6: Update sessionStorage when message from different conversation
                                    sessionStorage.setItem('messageCount', String(newCount));
                                    sessionStorage.setItem('messageCountTimestamp', String(Date.now()));
                                    sessionStorage.setItem('messageCountUserId', String(user?.id));
                                    
                                    return newCount;
                                });
                                
                                // Show notification for message from different conversation
                                if (showNotification) {
                                    const senderName = messageData.sender?.name || 'Someone';
                                    showNotification(`New message from ${senderName}`, 'info');
                                }
                            }, 600); // 600ms delay to allow MessengerPage auto-mark to complete first
                            return;
                        } else {
                            // User is NOT on messenger page - increment immediately
                            // Reset the last read time since we're receiving a new message
                            setLastMessagesReadTime(0);
                            
                            // Clear any debounce/pause that might block the update
                            // Pusher messages should always update immediately
                            pausePeriodicRefreshRef.current = false;
                            if (refreshDebounceTimerRef.current) {
                                // Handle both formats
                                if (typeof refreshDebounceTimerRef.current === 'object' && refreshDebounceTimerRef.current.timerId) {
                                    clearTimeout(refreshDebounceTimerRef.current.timerId);
                                } else {
                                    clearTimeout(refreshDebounceTimerRef.current);
                                }
                                refreshDebounceTimerRef.current = null;
                            }
                            
                        setMessageCount(prevCount => {
                            const newCount = prevCount + 1;
                            // Only log on first few messages to avoid spam
                            if (prevCount < 3) {
                                console.log('Dashboard: Message count updated from', prevCount, 'to', newCount, '(new message received via Pusher)');
                            } else {
                                console.log('Dashboard: Message count updated to', newCount, '(new message received via Pusher)');
                            }
                            
                            // SOLUTION 6: Update sessionStorage when new message arrives
                            sessionStorage.setItem('messageCount', String(newCount));
                            sessionStorage.setItem('messageCountTimestamp', String(Date.now()));
                            sessionStorage.setItem('messageCountUserId', String(user?.id));
                            
                            // Force a re-render by updating a timestamp
                            if (window.debugMessageCounter) {
                                window.debugMessageCounter.lastUpdate = Date.now();
                            }
                            
                            return newCount;
                        });
                            
                            // Show a notification for the new message
                            if (showNotification) {
                                const senderName = messageData.sender?.name || 'Someone';
                                showNotification(`New message from ${senderName}`, 'info');
                            }
                        }
                    }
                }
            });
            
            if (!cleanup) {
                console.error('Dashboard: Failed to set up message subscription - cleanup function is null');
            } else {
                console.log('Dashboard: Message subscription setup successful');
            }
            
            return () => {
                if (cleanup) cleanup();
            };
        }
    }, [isConnected, user?.id, subscribeToMessageNotifications, showNotification]);
    
    // Removed automatic counter clearing on messenger navigation
    // Counter should only be cleared when messages are actually marked as read
    // via the messagesRead event from MessengerPage
    // (cancelPendingRefresh is now defined earlier in the file before navigation detection)
    
    // Enhanced function to refresh message count from server with retry logic
    // Now includes race condition protection, request cancellation, and debouncing
    const refreshMessageCount = useCallback(async (retryCount = 0, forceRefresh = false, skipProtection = false) => {
        const maxRetries = 3;
        const isParentUser = user?.roles?.some(role => role.name === "parent");
        const isMentorUser = user?.roles?.some(role => role.name === "mentor");
        
        // Skip message count refresh for mentors - they don't participate in regular messaging
        if (isMentorUser) {
            console.log('Dashboard: Skipping message count refresh for mentor user');
            setMessageCount(0); // Mentors always have 0 message count
            return 0;
        }
        
        // RACE CONDITION PROTECTION: Don't refresh if messages were recently read
        // unless this is a forced refresh or protection is explicitly skipped
        // Extended protection window to 15 seconds for production/staging environments
        const timeSinceLastRead = Date.now() - lastMessagesReadTime;
        const recentlyReadThreshold = 15000; // 15 seconds protection window (increased from 10s)
        
        if (!skipProtection && !forceRefresh && lastMessagesReadTime > 0 && timeSinceLastRead < recentlyReadThreshold) {
            console.log('Dashboard: Skipping refresh - messages were recently read (protection active)', {
                timeSinceLastRead: `${timeSinceLastRead}ms`,
                threshold: `${recentlyReadThreshold}ms`,
                lastReadTime: new Date(lastMessagesReadTime).toISOString(),
                paused: pausePeriodicRefreshRef.current
            });
            return messageCount; // Return current count without API call
        }
        
        // Cancel any pending refresh request
        cancelPendingRefresh();
        
        // Create new AbortController for this request
        refreshAbortControllerRef.current = new AbortController();
        const abortSignal = refreshAbortControllerRef.current.signal;
        
        try {
            console.log('Dashboard: Refreshing message count from server (attempt', retryCount + 1, ')');
            
            // Determine target user ID (current user or selected child for parents)
            const targetUserId = isParentUser 
                ? window.localStorage.getItem('SELECTED_CHILD_ID') || user.id
                : user.id;
            
            // Use dedicated unread count endpoint for efficiency
            // Support AbortController for request cancellation
            const response = await axiosInstance.get(`/unread_message_count/${targetUserId}`, {
                signal: abortSignal
            }).catch(error => {
                // Handle abort/cancellation gracefully
                if (error.name === 'AbortError' || error.name === 'CanceledError' || 
                    (error.code === 'ERR_CANCELED') || 
                    (error.message && error.message.includes('cancel'))) {
                    console.log('Dashboard: Refresh request was cancelled');
                    throw new Error('Request cancelled');
                }
                throw error;
            });
            
            console.log("🚀 ~ Dashboard ~ response:", response);
            
            if (response.data?.unread_count !== undefined) {
                const newCount = response.data.unread_count;
                console.log('Dashboard: Server reports', newCount, 'unread messages for user', targetUserId);
                
                // Enhanced race condition protection: Don't update if we just read messages
                // Extended window to 10 seconds for production database replication lag
                const veryRecentRead = Date.now() - lastMessagesReadTime < 10000; // 10 second window (increased from 3s)
                if (!skipProtection && !forceRefresh && veryRecentRead && newCount > 0 && messageCount === 0) {
                    console.log('Dashboard: Ignoring server count - potential database lag after recent read', {
                        serverCount: newCount,
                        currentCount: messageCount,
                        timeSinceRead: `${Date.now() - lastMessagesReadTime}ms`,
                        protectionWindow: '10s'
                    });
                    return messageCount; // Keep current count
                }
                
                // Only update if count actually changed to prevent unnecessary re-renders
                setMessageCount(prevCount => {
                    if (prevCount !== newCount) {
                        console.log('Dashboard: Message count updated:', prevCount, '->', newCount);
                        
                        // SOLUTION 6: Update sessionStorage when count changes
                        sessionStorage.setItem('messageCount', String(newCount));
                        sessionStorage.setItem('messageCountTimestamp', String(Date.now()));
                        sessionStorage.setItem('messageCountUserId', String(user?.id));
                        
                        return newCount;
                    }
                    return prevCount;
                });
                return newCount;
            } else {
                throw new Error('Invalid response format from unread count endpoint');
            }
        } catch (error) {
            // Don't log cancellation as an error
            if (error.message === 'Request cancelled') {
                return messageCount;
            }
            
            console.error('Dashboard: Failed to refresh message count (attempt', retryCount + 1, '):', error);
            
            // Retry logic with exponential backoff
            if (retryCount < maxRetries) {
                const delayMs = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
                console.log(`Dashboard: Retrying message count refresh in ${delayMs}ms...`);
                
                setTimeout(() => {
                    // Check if we should still retry (protection might have changed)
                    refreshMessageCount(retryCount + 1, forceRefresh, skipProtection);
                }, delayMs);
            } else {
                console.error('Dashboard: All retry attempts failed for message count refresh');
                
                // Fallback to auth/me endpoint as last resort
                try {
                    console.log('Dashboard: Attempting fallback to /auth/me endpoint');
                    const fallbackResponse = await axiosInstance.get(`/auth/me`);
                    if (fallbackResponse.data?.message_notifs) {
                        const fallbackCount = fallbackResponse.data.message_notifs.length;
                        console.log('Dashboard: Fallback reports', fallbackCount, 'unread messages');
                        setMessageCount(fallbackCount);
                        return fallbackCount;
                    }
                } catch (fallbackError) {
                    console.error('Dashboard: Fallback also failed:', fallbackError);
                }
            }
        } finally {
            // Clear abort controller after request completes
            if (refreshAbortControllerRef.current && !abortSignal.aborted) {
                refreshAbortControllerRef.current = null;
            }
        }
        return messageCount;
    }, [messageCount, user?.id, user?.roles, lastMessagesReadTime, cancelPendingRefresh]);
    
    // Listen for custom events when messages are read
    useEffect(() => {
        const handleMessagesRead = (event) => {
            console.log('Dashboard: Received messagesRead event', event.detail);
            const { updatedCount } = event.detail || {};
            
            // CRITICAL: Cancel any pending refresh requests immediately
            cancelPendingRefresh();
            
            // Pause periodic refresh to prevent race conditions
            pausePeriodicRefreshRef.current = true;
            
            // Record the timestamp when messages were read to prevent race conditions
            const readTime = Date.now();
            setLastMessagesReadTime(readTime);
            lastMessagesReadTimeRef.current = readTime; // Update ref for use in closures
            
            // SOLUTION 6: Persist to sessionStorage to survive page refreshes
            sessionStorage.setItem('lastMessagesReadTime', String(readTime));
            sessionStorage.setItem('lastMessagesReadTimeTimestamp', String(readTime));
            sessionStorage.setItem('lastMessagesReadTimeUserId', String(user?.id));
            
            console.log('Dashboard: Messages read at', new Date(readTime).toISOString());
            console.log('Dashboard: Periodic refresh paused to prevent race conditions');
            
            if (typeof updatedCount === 'number') {
                console.log('Dashboard: Setting message count to:', updatedCount, 'from messagesRead event (priority update)');
                setMessageCount(updatedCount);
                
                // SOLUTION 6: Update sessionStorage with fresh count
                sessionStorage.setItem('messageCount', String(updatedCount));
                sessionStorage.setItem('messageCountTimestamp', String(readTime));
                sessionStorage.setItem('messageCountUserId', String(user?.id));
                
                // SOLUTION 6: Update localStorage USER object with fresh message_notifs
                // This prevents stale data from being used on remount
                try {
                    const storedUser = window.localStorage.getItem('USER');
                    if (storedUser) {
                        const userData = JSON.parse(storedUser);
                        // Update message_notifs array to reflect read messages
                        // Keep only unread messages (updatedCount should match remaining unread)
                        if (userData.message_notifs && Array.isArray(userData.message_notifs)) {
                            // Filter to keep only unread messages (this is a simplified approach)
                            // The actual filtering should match what the server returns
                            // For now, we'll update the count and let the next API call refresh the array
                            userData.message_notifs = userData.message_notifs.slice(0, updatedCount);
                            window.localStorage.setItem('USER', JSON.stringify(userData));
                            console.log('Dashboard: Updated localStorage USER.message_notifs with fresh count:', updatedCount);
                        }
                    }
                } catch (error) {
                    console.error('Dashboard: Failed to update localStorage USER:', error);
                }
            } else {
                // Fallback: refresh from server (but still mark as recently read)
                console.log('Dashboard: No updated count provided, refreshing from server with protection');
                refreshMessageCount(0, true, false); // Force refresh but respect protection
            }
            
            // Resume periodic refresh after protection window expires
            setTimeout(() => {
                pausePeriodicRefreshRef.current = false;
                console.log('Dashboard: Periodic refresh resumed after protection window');
            }, 15000); // Match the protection window duration
        };
        
        window.addEventListener('messagesRead', handleMessagesRead);
        
        return () => {
            window.removeEventListener('messagesRead', handleMessagesRead);
        };
    }, [refreshMessageCount, cancelPendingRefresh, user?.id]);
    
    // Enhanced periodic refresh with adaptive frequency, pause support, and debouncing
    useEffect(() => {
        if (!user?.id) return;
        
        // Skip periodic refresh for mentors - they don't participate in regular messaging
        const isMentorUser = user?.roles?.some(role => role.name === "mentor");
        if (isMentorUser) {
            console.log('Dashboard: Skipping periodic message count refresh setup for mentor user');
            return;
        }
        
        // More conservative polling to reduce race conditions
        // Longer intervals reduce chance of conflicting with messagesRead events
        let intervalMs;
        if (isConnected) {
            intervalMs = isLoading ? 5000 : 60000; // 5s while loading, then 60s when stable (increased from 30s)
        } else {
            intervalMs = isLoading ? 3000 : 10000; // 3s while loading, then 10s when disconnected (increased from 5s)
        }
        
        // Setting up periodic message count refresh with pause support
        const interval = setInterval(() => {
            // Skip refresh if paused (e.g., after messagesRead event)
            if (pausePeriodicRefreshRef.current) {
                console.log('Dashboard: Skipping periodic refresh - paused due to recent messagesRead event');
                return;
            }
            
            // Debounce: Only refresh if no recent refresh is in progress
            // But allow refresh if debounce has been active for more than 5 seconds
            // This prevents the debounce from blocking refreshes indefinitely
            const debounceStartTime = refreshDebounceTimerRef.current?.startTime;
            const debounceActiveTooLong = debounceStartTime && (Date.now() - debounceStartTime > 5000);
            
            if (refreshDebounceTimerRef.current && !debounceActiveTooLong) {
                console.log('Dashboard: Skipping periodic refresh - debounce active');
                return;
            }
            
            // Clear old debounce timer if it's been active too long
            if (debounceActiveTooLong && refreshDebounceTimerRef.current) {
                clearTimeout(refreshDebounceTimerRef.current.timerId);
                refreshDebounceTimerRef.current = null;
            }
            
            // Set debounce timer to prevent rapid successive calls
            // Store both the timer ID and start time for timeout detection
            const timerId = setTimeout(() => {
                refreshDebounceTimerRef.current = null;
            }, 2000); // 2 second debounce window
            
            refreshDebounceTimerRef.current = {
                timerId: timerId,
                startTime: Date.now()
            };
            
            refreshMessageCount();
        }, intervalMs);
        
        return () => {
            clearInterval(interval);
            if (refreshDebounceTimerRef.current) {
                // Handle both formats (old: just timer ID, new: object with timerId)
                if (typeof refreshDebounceTimerRef.current === 'object' && refreshDebounceTimerRef.current.timerId) {
                    clearTimeout(refreshDebounceTimerRef.current.timerId);
                } else {
                    clearTimeout(refreshDebounceTimerRef.current);
                }
                refreshDebounceTimerRef.current = null;
            }
        };
    }, [isConnected, user?.id, isLoading, refreshMessageCount]);
    
    // Immediate forced refresh when user logs in to show unread messages immediately
    useEffect(() => {
        if (!propsIsLoading && user && !isLoading) {
            // Check if this is a fresh login by comparing with previous user ID
            const currentUserId = user.id;
            const prevUserId = isFreshLoginRef.current?.userId;
            const isFreshLogin = prevUserId !== currentUserId;
            
            // Update the ref with current user ID
            if (isFreshLogin) {
                isFreshLoginRef.current = { userId: currentUserId, isFresh: true };
            }
            
            // Clear any existing debounce timer and refresh state
            if (refreshDebounceTimerRef.current) {
                // Handle both old format (just timer ID) and new format (object with timerId)
                if (typeof refreshDebounceTimerRef.current === 'object' && refreshDebounceTimerRef.current.timerId) {
                    clearTimeout(refreshDebounceTimerRef.current.timerId);
                } else {
                    clearTimeout(refreshDebounceTimerRef.current);
                }
                refreshDebounceTimerRef.current = null;
            }
            
            // Clear any pause state that might block refresh
            pausePeriodicRefreshRef.current = false;
            
            // Reset last read time to allow refresh (fresh login = no recent reads)
            if (isFreshLogin) {
                setLastMessagesReadTime(0);
                lastMessagesReadTimeRef.current = 0;
                
                // SOLUTION 6: Clear sessionStorage for lastMessagesReadTime on fresh login
                sessionStorage.removeItem('lastMessagesReadTime');
                sessionStorage.removeItem('lastMessagesReadTimeTimestamp');
                sessionStorage.removeItem('lastMessagesReadTimeUserId');
            }
            
            if (isFreshLogin && isFreshLoginRef.current.isFresh) {
                // Fresh login detected - force immediate refresh bypassing all protection
                isFreshLoginRef.current.isFresh = false; // Mark as processed
                console.log('Dashboard: Fresh login detected - forcing immediate message count refresh (bypassing all protection)...');
                
                // Force refresh immediately with no delay, bypassing all protection
                refreshMessageCount(0, true, true); // forceRefresh=true, skipProtection=true
            } else {
                // Not a fresh login - just a component remount or navigation
                // Use a small delay to allow component to stabilize, but still refresh
                const timerId = setTimeout(() => {
                    refreshDebounceTimerRef.current = null;
                    
                    // If messageCount is 0 but we suspect there might be messages, refresh immediately
                    if (messageCount === 0) {
                        console.log('Dashboard: Counter is 0 after component mount, performing refresh...');
                        refreshMessageCount(0, true, false); // Force refresh but respect protection
                    }
                    // Also do a safety refresh regardless to ensure accuracy
                    else {
                        console.log('Dashboard: Performing safety refresh after component mount...');
                        refreshMessageCount(0, true, false); // Force refresh but respect protection
                    }
                }, 100); // Reduced delay to 100ms for faster refresh
                
                refreshDebounceTimerRef.current = timerId; // Store timer ID in old format for this use case
            }
            
            return () => {
                if (refreshDebounceTimerRef.current) {
                    // Handle both formats
                    if (typeof refreshDebounceTimerRef.current === 'object' && refreshDebounceTimerRef.current.timerId) {
                        clearTimeout(refreshDebounceTimerRef.current.timerId);
                    } else {
                        clearTimeout(refreshDebounceTimerRef.current);
                    }
                    refreshDebounceTimerRef.current = null;
                }
            };
        }
    }, [user?.id, propsIsLoading, isLoading, refreshMessageCount, messageCount]);
    
    
    const SimpleMenu = () => {
        const [open, setOpen] = useState(false);
        const [notifCount, setNotifCount] = useState(0);
        const anchorRef = useRef(null);
        const { isConnected, subscribeToMenuNotifications } = usePusherConnection(user);
        const classes = useStyles();
        const history = useHistory();
        
        // Add this function to handle switching child accounts
        const handleSwitchChild = (event) => {
            handleClose(event);
            // Navigate to the child selection component with a clean URL to ensure the component knows this is a switch operation
            history.push('/select-kid');
        };
        
        const fetchNotificationCount = useCallback(async () => {
            try {
                if (isParent) {
                    const selectedChildId = window.localStorage.getItem('SELECTED_CHILD_ID');
                    console.log('Dashboard: Fetching notification count for child:', selectedChildId);
                    
                    // Pass parent_id parameter for parent-aware filtering
                    // Use parentId (original parent ID) if available, otherwise fall back to user.id
                    const parentId = user.parentId || user.id;
                    const url = `/pending_friends/${selectedChildId}?parent_id=${parentId}`;
                    console.log('Dashboard: Fetching with parent-aware URL:', url);
                    
                    const result = await axiosInstance.get(url);
                    console.log('Dashboard: Pending friends result (parent-filtered):', result.data);
                    setNotifCount(result.data.length);
                }
            } catch (error) {
                console.error('Error fetching notification count:', error);
                setNotifCount(0); // Reset to 0 on error
            }
        }, [isParent, user?.id]);
        
        useEffect(() => {
            fetchNotificationCount();
        }, [fetchNotificationCount, isParent]);
        
        // Also refresh notification count when selected child changes (for parents)
        useEffect(() => {
            if (isParent) {
                const handleStorageChange = (e) => {
                    if (e.key === 'SELECTED_CHILD_ID') {
                        console.log('Dashboard: Selected child changed, refreshing notification count');
                        setTimeout(fetchNotificationCount, 100); // Small delay to ensure localStorage is updated
                    }
                };
                
                window.addEventListener('storage', handleStorageChange);
                
                // Also listen for manual localStorage changes (same window)
                const originalSetItem = localStorage.setItem;
                localStorage.setItem = function(key, value) {
                    originalSetItem.apply(this, arguments);
                    if (key === 'SELECTED_CHILD_ID') {
                        console.log('Dashboard: Selected child changed (same window), refreshing notification count');
                        setTimeout(fetchNotificationCount, 100);
                    }
                };
                
                return () => {
                    window.removeEventListener('storage', handleStorageChange);
                    localStorage.setItem = originalSetItem;
                };
            }
        }, [isParent, fetchNotificationCount]);
        
        useEffect(() => {
            
            if (isConnected && isParent) {
                const cleanup = subscribeToMenuNotifications((data) => {
                    const selectedChildId = window.localStorage.getItem('SELECTED_CHILD_ID');
                    
                    console.log('Dashboard: Menu notification received:', data);
                    
                    if (data.child_id === selectedChildId) {
                        if (data.type === 'friend_request_completed') {
                            // Refresh notification count when friend request is completed
                            console.log('Dashboard: Friend request completed, refreshing count');
                            fetchNotificationCount();
                            showNotification(
                                `Friend request approved! ${data.friend_name || 'Someone'} is now friends with your child.`,
                                'success'
                            );
                        } else if (data.type === 'friend_request_cancelled') {
                            // Refresh notification count when friend request is cancelled
                            console.log('Dashboard: Friend request cancelled, refreshing count');
                            fetchNotificationCount();
                            showNotification(
                                `Friend request with ${data.friend_name || 'someone'} has been cancelled.`,
                                'info'
                            );
                        } else if (data.type === 'friend_request_partial_approval') {
                            // Update counter when parent gives partial approval
                            console.log('Dashboard: Partial approval notification received, updating count to:', data.count);
                            setNotifCount(data.count || 0);
                            showNotification(
                                `Your approval recorded. Waiting for the other parent's approval.`,
                                'info'
                            );
                        } else {
                            // New friend request
                            setNotifCount(prev => prev + 1);
                            showNotification(
                                `New friend request for your child from ${data.friend_name || 'someone'}`,
                                'info'
                            );
                        }
                    }
                });
                
                return cleanup;
            }
        }, [user, isConnected, subscribeToMenuNotifications, fetchNotificationCount, showNotification]);
        
        const handleToggle = () => {
            setOpen((prevOpen) => !prevOpen);
        };
        
        const handleClose = (event) => {
            if (anchorRef.current && anchorRef.current.contains(event.target)) {
                return;
            }
            setOpen(false);
        };
        
        const handleFriendRequests = (event) => {
            handleClose(event);
            history.push('/dashboard/xc_pals');
        };
        
        const handleLogout = (event) => {
            handleClose(event);
            Object.keys(localStorage).forEach(key => localStorage.removeItem(key));
            window.location = REACT_APP_API_URL.includes('localhost') ? 'http://localhost:3000/' : 'https://www.bridgepalkids.com/';
        };
        
        
        return (
            <Badge
                badgeContent={isParent && notifCount > 0 ? notifCount : null}
                classes={{ badge: classes.badge }}
                color="primary"
                overlap="rectangular"
                invisible={!isParent || notifCount === 0}
            >
                <IconButton
                    ref={anchorRef}
                    aria-controls={open ? 'menu-list-grow' : undefined}
                    aria-haspopup="true"
                    style={{ color: theme.primaryColor }}
                    onClick={handleToggle}
                >
                    <MoreVertIcon />
                </IconButton>
                <Popper
                    open={open}
                    anchorEl={anchorRef.current}
                    role={undefined}
                    placement="bottom-end"
                    disablePortal
                    style={{ zIndex: 1300 }}
                >
                    <Paper 
                        className={`${classes.menuPaper} ${open ? classes.menuPaperVisible : ''}`}
                        elevation={4}
                    >
                        <ClickAwayListener onClickAway={handleClose}>
                            <MenuList autoFocusItem={open} id="menu-list-grow">
                                {/* Add Switch Child option for parents */}
                                {isParent && (
                                    <MenuItem onClick={handleSwitchChild}>
                                        Switch Child
                                    </MenuItem>
                                )}
                                {isParent && notifCount > 0 && (
                                    <MenuItem onClick={handleFriendRequests}>
                                        Friend Requests ({notifCount})
                                    </MenuItem>
                                )}
                                <MenuItem onClick={handleLogout}>
                                    Logout
                                </MenuItem>
                            </MenuList>
                        </ClickAwayListener>
                    </Paper>
                </Popper>
            </Badge>
        );
    };
    
    const PANELS = [
        <Home user={user} isParent={isParent} isMentor={isMentor} theme={theme} showNotification={showNotification} />,
        <LifeTopics user={user} isParent={isParent} isMentor={isMentor} theme={theme} />,
        <XCPals user={user} isParent={isParent} isMentor={isMentor} theme={theme} showNotification={showNotification} />,
        <Journal user={user} isParent={isParent} isMentor={isMentor} theme={theme} />,
        <OnlineResources user={user} isParent={isParent} isMentor={isMentor} theme={theme} />,
        <ParentsPortal user={user} isParent={isParent} isMentor={isMentor} theme={theme} />
    ];
    
    if (isLoading || propsIsLoading) {
        return (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }} className={classes.loadingContainer}>
                <CircularProgress />
                <p>Loading dashboard...</p>
            </div>
        );
    }
    
    if (!user) {
        return (
            <div className={classes.loadingContainer}>
                <p>Error: User data not available. Please try refreshing the page.</p>
            </div>
        );
    }
    
    // Debug utility - can be called from browser console
    window.debugMessageCounter = {
        getCurrentState: () => {
            console.log('=== MESSAGE COUNTER DEBUG INFO ===');
            console.log('Current messageCount state:', messageCount);
            console.log('Last messages read time:', lastMessagesReadTime, new Date(lastMessagesReadTime).toISOString());
            console.log('Time since last read:', Date.now() - lastMessagesReadTime, 'ms');
            console.log('Current settings state:', settings);
            console.log('Settings from localStorage:', JSON.parse(window.localStorage.getItem('SETTINGS') || 'null'));
            console.log('User object:', user);
            console.log('User message_notifs:', user?.message_notifs);
            console.log('User message_notifs length:', user?.message_notifs?.length);
            console.log('User roles:', user?.roles);
            console.log('Pusher connected:', isConnected);
            console.log('Badge element:', document.querySelector('[data-testid="message-badge"]'));
            console.log('Badge content:', document.querySelector('[data-testid="message-badge"]')?.textContent);
            console.log('Chat icon:', document.querySelector('[data-testid="chat-icon"]'));
            console.log('Chat debug element:', document.querySelector('[data-testid="chat-disabled-debug"]'));
            return {
                messageCount,
                lastMessagesReadTime,
                timeSinceLastRead: Date.now() - lastMessagesReadTime,
                settings,
                userMessageNotifs: user?.message_notifs,
                isPusherConnected: isConnected,
                badgeElement: document.querySelector('[data-testid="message-badge"]'),
                chatIconElement: document.querySelector('[data-testid="chat-icon"]'),
                lastUpdate: window.debugMessageCounter?.lastUpdate
            };
        },
        simulateMessageReceived: () => {
            console.log('Simulating message received...');
            setMessageCount(prev => {
                const newCount = prev + 1;
                console.log('Simulated message count update:', prev, '->', newCount);
                return newCount;
            });
        },
        forceSettingsRefresh: () => {
            console.log('Force refreshing settings...');
            const userSettings = user?.settings;
            if (userSettings) {
                setSettings(userSettings);
                window.localStorage.setItem('SETTINGS', JSON.stringify(userSettings));
                console.log('Settings refreshed:', userSettings);
            } else {
                console.warn('No user.settings available to refresh');
            }
        },
        enableChat: () => {
            console.log('Manually enabling chat...');
            const newSettings = { ...settings, chat_allowed: 1 };
            setSettings(newSettings);
            window.localStorage.setItem('SETTINGS', JSON.stringify(newSettings));
            console.log('Chat enabled manually');
        },
        refreshMessageCount: async (force = false) => {
            console.log('Manually refreshing message count...');
            return await refreshMessageCount(0, force);
        },
        clearMessageCount: () => {
            console.log('Manually clearing message count');
            setMessageCount(0);
        },
        clearReadProtection: () => {
            console.log('Manually clearing read protection window');
            setLastMessagesReadTime(0);
        },
        forceRefresh: async () => {
            console.log('Force refreshing message count (ignoring protection)');
            return await refreshMessageCount(0, true);
        },
        testPusherConnection: () => {
            console.log('=== PUSHER CONNECTION TEST ===');
            console.log('Window.Echo:', window.Echo);
            console.log('Pusher state:', window.Echo?.connector?.pusher?.connection?.state);
            console.log('IsConnected state:', isConnected);
            if (window.Echo && user?.id) {
                try {
                    const testChannel = window.Echo.private(`messages.${user.id}`);
                    console.log('Successfully created test channel:', testChannel);
                    return testChannel;
                } catch (error) {
                    console.error('Error creating test channel:', error);
                    return null;
                }
            }
        },
        checkRecentMessages: async () => {
            console.log('=== CHECKING RECENT MESSAGES ===');
            try {
                const response = await axiosInstance.get(`/user/${user.id}`);
                console.log('User data from backend:', response.data);
                console.log('Message notifications:', response.data.message_notifs);
                return response.data;
            } catch (error) {
                console.error('Error fetching user data:', error);
                return null;
            }
        },
        testSendMessage: async (recipientId, content = 'Test message') => {
            console.log('=== TESTING MESSAGE SEND ===');
            try {
                const response = await axiosInstance.post(
                    `/send_message/${recipientId}`,
                    { content }
                );
                console.log('Message send response:', response.data);
                return response.data;
            } catch (error) {
                console.error('Error sending test message:', error);
                return null;
            }
        },
        refreshUserData: async () => {
            console.log('=== REFRESHING USER DATA ===');
            try {
                const response = await axiosInstance.get(`/auth/me`);
                console.log('Fresh user data from /auth/me:', response.data);
                console.log('Fresh message_notifs:', response.data.message_notifs);
                console.log('Fresh message_notifs length:', response.data.message_notifs?.length);
                return response.data;
            } catch (error) {
                console.error('Error refreshing user data:', error);
                return null;
            }
        }
    };
    
    return (
        <div>
            <Grid container justifyContent="flex-end" className={classes.root} spacing={2}>
                <Grid item xs={9}>
                    <img
                        style={{
                            marginLeft: "10px",
                            width: "256px",
                            height: "76px"
                        }}
                        src="/icons/BridgePal-Stacked.svg"
                        alt={user.gender === "F" ? "XcBrgPalGirls" : "XcBrgPalBoys"}
                    />
                </Grid>
                
                <Grid item xs={1}>
                    <Badge
                        badgeContent={messageCount > 0 ? messageCount : null}
                        classes={{ badge: classes.notifBadge }}
                        color="primary"
                        overlap="rectangular"
                        data-testid="message-badge"
                    >
                        {settings && settings?.chat_allowed === 1 ? (
                            <img
                                style={{
                                    width: "24px",
                                    height: "24px",
                                    paddingLeft: "76px",
                                    paddingTop: "20px",
                                    cursor: "pointer"
                                }}
                                onClick={() => {
                                    console.log('Dashboard: Messenger icon clicked - navigating to messenger');
                                    // Don't clear counter here - let MessengerPage handle it after messages are actually read
                                    history.push("/messenger");
                                }}
                                src="/icons/chat.png"
                                alt="Chat"
                                data-testid="chat-icon"
                            />
                        ) : (
                            // Debug element to help identify the issue
                            <div
                                style={{
                                    width: "24px",
                                    height: "24px",
                                    paddingLeft: "76px",
                                    paddingTop: "20px",
                                    fontSize: "10px",
                                    color: "red",
                                    textAlign: "center"
                                }}
                                title={`Settings: ${JSON.stringify(settings)} | chat_allowed: ${settings?.chat_allowed}`}
                                data-testid="chat-disabled-debug"
                            >
                                {!settings ? 'No Settings' : settings?.chat_allowed === 0 ? 'Chat Disabled' : 'Chat Issue'}
                            </div>
                        )}
                    </Badge>
                </Grid>
                <Grid
                    item
                    className={classes.topNav}
                    xs={1}
                >
                    <BrAvatar
                        user={user}
                        theme={theme}
                        label={user.name}
                        onClick={() => history.push("/user/" + user.id)}
                        avatarStyles={{cursor: "pointer"}}
                    />
                </Grid>
                <Grid item xs={1}>
                    <SimpleMenu />
                </Grid>
                <NavTabs
                    user={user}
                    isParent={isParent}
                    isMentor={isMentor}
                    setValue={handleTabChange}
                    value={value}
                    panels={PANELS}
                    theme={theme}
                />
            </Grid>
            
            {PANELS[initialTab ? initialTab : value]}
        </div>
    );
};